import type { ParticipantNote } from '@seen/core/models/participantNote'
import { VisitNote } from '@seen/core/models/visitNote'
import type { JSX } from 'react'
import { TextLink } from '#src/components/index.ts'
import { useErrorToast } from '#src/hooks/index.ts'
import { useVisitNoteHref } from '#src/hooks/useVisitNoteHref.ts'
import { ParticipantNoteTimelineItem } from '#src/pages/participants/timeline/ParticipantNoteTimelineItem.tsx'
import { TimelineNoteSummary } from '#src/pages/participants/timeline/TimelineNoteSummary.tsx'
import { TimelineTextEntry } from '#src/pages/participants/timeline/TimelineTextEntry.tsx'

interface VisitNoteItemProps {
  visitNote: VisitNote | ParticipantNote
}

export function VisitNoteItem({ visitNote }: VisitNoteItemProps): JSX.Element {
  if (visitNote instanceof VisitNote) {
    return <ElationVisitNoteItem visitNote={visitNote} />
  }

  return <ParticipantNoteTimelineItem participantNote={visitNote} />
}

function ElationVisitNoteItem({
  visitNote,
}: {
  visitNote: VisitNote
}): JSX.Element {
  useErrorToast(
    !visitNote.practitioner && `Visit note ${visitNote.id} has no practitioner`
  )

  useErrorToast(!visitNote.date && `Visit note ${visitNote.id} has no date`)

  const href = useVisitNoteHref(visitNote)

  return (
    <TimelineTextEntry
      actor={visitNote.signedBy ?? visitNote.practitioner ?? 'Unknown'}
      time={visitNote.signedDate ?? visitNote.date ?? null}
      action={
        <>
          {visitNote.isSigned ? 'signed an' : 'created an'}{' '}
          {href && <TextLink to={href}>Elation visit note</TextLink>}
          {!href && 'Elation visit note'}
        </>
      }
      reference={visitNote.reference}
      originTask={visitNote.queuedEditsOriginTask}
      detail={
        <TimelineNoteSummary
          aiSummary={visitNote.aiSummary}
          aiSummaryGeneratedAt={visitNote.aiSummaryGeneratedAt}
          isSigned={visitNote.isSigned}
        />
      }
    />
  )
}
